import { difference, has } from 'lodash-es';

export const getCurrencyLabel = (currencyCode, locale = 'en-US') => {
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(0);

  const symbol = parts.find((p) => p.type === 'currency')?.value || currencyCode;

  return symbol === currencyCode ? currencyCode : `${symbol} ${currencyCode}`;
};

export const convertToSeconds = ({ days, hours, minutes }, isNegative = false) => {
  const seconds = days * 86400 + hours * 3600 + minutes * 60;
  return isNegative ? seconds * -1 : seconds;
};

export const convertFromSeconds = (seconds) => {
  let s = seconds < 0 ? seconds * -1 : seconds;
  const days = Math.floor(s / 86400);
  s %= 86400;

  const hours = Math.floor(s / 3600);
  s %= 3600;

  const minutes = Math.floor(s / 60);
  s %= 60;

  return { days, hours, minutes, s };
};

export const formatNumberInput = (value) => {
  const trimmedValue = value.replace(/^0+(?=\d)/, '');

  const sanitizedValue = trimmedValue.replace(/[^0-9.,]/g, '');

  const dotValue = sanitizedValue.replace(/^[.,]+/, '');

  const standardizedValue = dotValue.replace(/,/g, '.');

  const regex = /^(\d*\.?\d{0,2}).*$/;

  const formattedValue = standardizedValue.replace(regex, '$1');

  return formattedValue.substring(0, 20);
};

export const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.getMonth() + 1; // Months are 0-indexed
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  return `${day}.${month}.${year}. / ${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
};

export const formatDateTimeForInput = (dateTimeString) => {
  if (!dateTimeString) return null;

  const date = new Date(dateTimeString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}`;
};

export const formatDateToIsoString = (dateTimeString) => new Date(dateTimeString).toISOString();

export const filterValidKeys = (raw) =>
  Object.keys(raw)
    .filter((key) => {
      if (raw[key] && Array.isArray(raw[key])) return raw[key].length;
      if (raw[key] && typeof raw[key] === 'object') return Object.keys(raw[key]).length;

      if (key === 'betBuilder')
        return typeof raw[key] !== 'undefined' && raw[key] !== null && raw[key] !== '';

      return raw[key] && raw[key] !== '';
    })
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});

export const filterAllowedKeys = (raw, keyBlacklist) =>
  Object.keys(raw)
    .filter((key) => !keyBlacklist?.includes(key))
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});

export const formatFeedName = (name) => name.split(' ')[0];

export const isFeedOpenForBettingStatus = (feedId, feedData) => {
  const foundFeed = feedData.find((feed) => feed.feedId === feedId);

  if (!foundFeed) return true;

  return !!foundFeed.openForBettingStatus;
};

export const filterExcludedParams = (paramObject, excludedParams) => {
  const validParams = filterValidKeys(paramObject);
  return difference(excludedParams, Object.keys(validParams));
};

export const setSupplementalNamesData = (item) =>
  item.supplementalNames.reduce(
    (data, { name, translatedName, supplementalNameTypeId }) =>
      Object.assign(data, {
        [`supplementalName_${supplementalNameTypeId}`]: name,
        [`translatedSupplementalName_${supplementalNameTypeId}`]: translatedName,
      }),
    {},
  );

export const filterSupplementalNameKeys = (item) => {
  const isSupplementalName = (key) =>
    item[key] !== null &&
    item[key] !== undefined &&
    key.split('_')[0] === 'supplementalName' &&
    key.split('_').length > 1;

  return Object.keys(item).filter(isSupplementalName);
};

export const formatTranslationsForUpdate = (data) =>
  data?.map((item) => ({
    name: item.name === '' ? null : item.name,
    info: item.info === '' ? null : item.info,
    languageCode: item.languageCode,
    ...(has(item, 'eventMarketName') && {
      eventMarketName: item.eventMarketName === '' ? null : item.eventMarketName,
    }),
    ...(has(item, 'eventMarketOutcomeName') && {
      eventMarketOutcomeName:
        item.eventMarketOutcomeName === '' ? null : item.eventMarketOutcomeName,
    }),
    supplementalNames: filterSupplementalNameKeys(item).map((key) => ({
      name: item[key] === '' ? null : item[key],
      supplementalNameTypeId: parseInt(key.split('_')[1]),
    })),
  })) || [];

export const formatMultipleSupplementalNameFields = (item) => {
  const supplementalNameKeys = filterSupplementalNameKeys(item);

  return supplementalNameKeys.reduce((acc, key) => {
    const match = key.match(/supplementalName_(\d+)_(\w+)/);
    if (match) {
      const [, id, field] = match;
      const existingEntry = acc.find((entry) => entry.supplementalNameTypeId === Number(id));

      if (existingEntry) {
        existingEntry[field] = item[key] === '' ? null : item[key];
      } else {
        acc.push({
          supplementalNameTypeId: Number(id),
          [field]: item[key] === '' ? null : item[key],
        });
      }
    }
    return acc;
  }, []);
};

export const formatDefaultCellPayload = (columnKey, editKey, value) => {
  const [editedObject, editedKey] = editKey ? editKey.split('_') : [];
  const payload = { sourcePolicyMap: {} };

  payload.sourcePolicyMap[editedObject || columnKey] = editedObject ? { [editedKey]: 2 } : 2;

  payload[editedObject || columnKey] = editedObject ? { [editedKey]: value } : value;

  return payload;
};

export const formatSupplementalNameCellPayload = (columnKey, editKey, value, field = 'name') => {
  const id = columnKey.split('_')[1];

  return {
    [editKey]: [
      {
        [field]: value || null,
        supplementalNameTypeId: id,
      },
    ],
  };
};

export const formatTranslationCellPayload = (
  columnKey,
  editKey,
  translationKey,
  value,
  language = 'en',
  supplementalNameField = 'name',
) => {
  const supplementalNameTypeId = columnKey.split('_')[1];
  const translatedFieldName = translationKey.split('_')[1];

  return {
    [editKey]: [
      {
        languageCode: language || 'en',
        ...(supplementalNameTypeId
          ? formatSupplementalNameCellPayload(
              columnKey,
              'supplementalNames',
              value,
              supplementalNameField,
            )
          : { [translatedFieldName]: value || null }),
      },
    ],
  };
};

export const formatSupplementaNameSourcePolicyMap = (key) => {
  const supplementalNameTypeId = key.split('_')[1];

  return {
    supplementalNames: {
      [supplementalNameTypeId]: {
        name: 2,
      },
    },
  };
};

export const formatSupplementalNameEditPayload = ({
  supplementalNameList,
  supplementalNameTypeId,
  value,
  key,
}) => {
  let supplementalNameListCopy = supplementalNameList ? [...supplementalNameList] : null;

  if (!supplementalNameListCopy) {
    supplementalNameListCopy = [
      {
        [key]: value,
        supplementalNameTypeId,
      },
    ];
  }

  let foundMatch = false;

  supplementalNameListCopy.forEach((obj, index) => {
    if (supplementalNameListCopy[index].supplementalNameTypeId === supplementalNameTypeId) {
      supplementalNameListCopy[index][key] = value;
      foundMatch = true;
    }
  });

  if (!foundMatch)
    supplementalNameListCopy.push({
      [key]: value,
      supplementalNameTypeId,
    });

  return supplementalNameListCopy;
};

export const setSupplementalNameTypesData = (
  supplementalNameTypes,
  supplementalNames,
  additionalField = '',
) => {
  return supplementalNameTypes.map((type) => {
    const foundSupplementalName = supplementalNames.find(
      ({ supplementalNameTypeId }) => supplementalNameTypeId === type.id,
    );

    return {
      ...(foundSupplementalName ?? {
        name: null,
        parentName: null,
        computedName: null,
        ...(additionalField === 'eventMarket' && {
          eventMarketName: null,
          computedEventMarketName: null,
          parentEventMarketName: null,
        }),
        ...(additionalField === 'eventMarketOutcome' && {
          eventMarketOutcomeName: null,
          computedEventMarketOutcomeName: null,
          parentEventMarketOutcomeName: null,
        }),
        supplementalNameTypeId: type.id,
      }),

      supplementalNameTypeName: type.name,
    };
  });
};

export const formatTranslationsSourcePolicyMap = (id, column) => {
  const { key, editKey } = column;

  if (editKey === 'supplementalNames') {
    return {
      translations: {
        [id]: formatSupplementaNameSourcePolicyMap(key),
      },
    };
  }

  return {
    translations: {
      [id]: {
        [key]: 2,
      },
    },
  };
};

export const hasSourceField = (item, field) => {
  if (item?.isTenantSourceOfEntity) return false;

  const capitalizedFieldName = field.charAt(0).toUpperCase() + field.slice(1);
  const formattedFieldName = `computed${capitalizedFieldName}`;

  return typeof item?.isParentSourceByField?.[formattedFieldName] === 'boolean';
};

export const hasSupplementalNameSourceField = (item, index, prop = 'Name') => {
  if (item.isTenantSourceOfEntity) return false;

  const isParentSourceByField =
    item.supplementalNames?.find((sn) => sn.supplementalNameTypeId === index)
      ?.isParentSourceByField ?? {};

  return typeof isParentSourceByField[`computed${prop}`] === 'boolean';
};

export const getStatusClass = (status) => {
  if (typeof status !== 'string') return;

  switch (status.toLowerCase()) {
    case 'won':
      return 'n-i-check-e won';
    case 'open':
      return 'n-i-clock open';
    case 'lost':
      return 'n-i-close-d lost';
    case 'half_won':
      return 'n-i-won-void-a half-won';
    case 'half_lost':
      return 'n-i-lost-void-a half-lost';
    case 'void':
      return 'n-i-void void';
    default:
      return '';
  }
};

export const getPhaseClass = (phase) => {
  if (typeof phase !== 'string') return;

  switch (phase.toLowerCase()) {
    case 'prepared':
      return 'n-i-refresh-a prepared';
    case 'placed':
      return 'n-i-check-e placed';
    case 'rejected':
      return 'n-i-void rejected';
    case 'cancelled':
      return 'n-i-void cancelled';
    case 'admin_cancelled':
      return 'n-i-void admin-cancelled';
    case 'settled':
      return 'n-i-check-e settled';
    case 'paid_out':
      return 'n-i-check-e paid-out';
    case 'expired':
      return 'n-i-void expired';
    default:
      return '';
  }
};

export const formatPhase = (phase) => {
  return phase.toLowerCase().replace('_', ' ');
};

export async function fetchMultiPageData(req) {
  let data = {};
  let response = await req({ page: 1, size: 100 });

  if (!response.data) return;
  data = response.data;

  if (response.paginationInfo.pages > 1) {
    for (let i = 2; i <= response.paginationInfo.pages; i++) {
      response = await req({ page: i, size: 100 });
      data.push(...response.data);
    }
  }

  return data;
}

export const normalizeAndRemoveDiacritics = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

export const formatAmount = (amount, fractionDigits = true) => {
  return parseFloat(amount || 0).toLocaleString(
    'en',
    fractionDigits && {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  );
};

/**
 * Formats a numeric amount with a specified currency, supporting both FIAT and crypto currencies.
 * Uses 'de-DE' locale for number formatting. If the currency is not supported by Intl.NumberFormat,
 * falls back to a generic number format and appends the currency code if requested.
 *
 * @param {Object} params - The parameters for formatting.
 * @param {number} params.amount - The numeric amount to format.
 * @param {string} params.currency - The currency code (e.g., 'EUR', 'USD', 'BTC').
 * @param {boolean} [params.displayCurrencySymbol=false] - Whether to display the currency symbol.
 * @param {Object} [params.formattingRules] - Optional formatting rules for fraction digits.
 * @param {number} [params.formattingRules.minimumFractionDigits=2] - Minimum number of fraction digits.
 * @param {number} [params.formattingRules.maximumFractionDigits=8] - Maximum number of fraction digits.
 * @returns {string} The formatted amount as a string, with currency symbol or code as specified.
 *
 * @example
 * formatNumberWithCurrency({ amount: 1234.56, currency: 'EUR', displayCurrencySymbol: true });
 * // Returns: "1.234,56 €"
 *
 * @example
 * formatNumberWithCurrency({ amount: 0.12345678, currency: 'BTC', displayCurrencySymbol: true });
 * // Returns: "0,12345678 BTC"
 */
export const formatNumberWithCurrency = ({
  amount,
  currency,
  displayCurrencySymbol = false,
  formattingRules = { minimumFractionDigits: 2, maximumFractionDigits: 8 },
}) => {
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);

    if (!displayCurrencySymbol) {
      // Remove everything except digits, comma, dot
      return formatted.replace(/[^0-9.,]/g, '').trim();
    }

    return formatted;
  } catch {
    // Fallback: crypto or unsupported currency
    const cryptoFormatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: formattingRules.minimumFractionDigits ?? 2,
      maximumFractionDigits: formattingRules.maximumFractionDigits ?? 8,
    }).format(amount);

    return displayCurrencySymbol ? `${cryptoFormatted} ${currency}` : cryptoFormatted;
  }
};
